import { API_BASE_URL } from '@synergy/config';
import { BonusSettlementStatus } from '@synergy/models';
import { Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PaloOverflowAction, PaloOverflowMenu } from '@synergy/components-palo';
import { PoolConfirmationModal } from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components';
import { ReactElement, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { AddSupplierModal } from '../supplier-overview/components/bonus-settlement-supplier-overview-add-supplier.modal';
import { bonusSettlementsCustomerOverviewDetailsSelector } from '../customer-overview/store/bonus-settlements-customer-overview-selector';
import {
    completeCustomers,
    fetchBonusSettlementsCustomerOverview,
    fetchBonusSettlementsCustomerOverviewDetails,
    reloadCustomers,
} from '../customer-overview/store/bonus-settlements-customer-overview-actions';
import { defaultSearchParamsSelector } from '../../../../store/default-searchparams-selector';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

interface CustomerHeaderActionsProps {
    bonusSettlementId: number;
}

/**
 * What the member side of a settlement can do: one next step, the rest in the
 * overflow menu (#2206).
 */
export const CustomerHeaderActions = (props: CustomerHeaderActionsProps): ReactElement | null => {
    const { bonusSettlementId } = props;
    const details = useAppSelector(bonusSettlementsCustomerOverviewDetailsSelector);
    const dispatch = useThunkDispatch();

    const defaultSearchParams = useAppSelector(defaultSearchParamsSelector);
    const [searchParams] = useSearchParams(defaultSearchParams);

    const [isOpenCompleteAll, setIsOpenCompleteAll] = useState<boolean>(false);
    const [isOpenReloadAll, setIsOpenReloadAll] = useState<boolean>(false);
    const [isOpenAddSupplier, setIsOpenAddSupplier] = useState<boolean>(false);

    const onCompleteAllCustomers = async () => {
        if (bonusSettlementId && +bonusSettlementId) {
            await dispatch(
                completeCustomers({
                    bonusSettlementId: +bonusSettlementId,
                    completeAll: true,
                    customerIds: undefined,
                })
            );
            dispatch(
                fetchBonusSettlementsCustomerOverview({
                    id: +bonusSettlementId,
                    searchParams: searchParams,
                })
            );
            dispatch(fetchBonusSettlementsCustomerOverviewDetails(+bonusSettlementId));
        }
        setIsOpenCompleteAll(false);
    };

    const onReloadAllCustomers = async () => {
        setIsOpenReloadAll(false);

        if (bonusSettlementId && +bonusSettlementId) {
            await dispatch(
                reloadCustomers({
                    bonusSettlementId: +bonusSettlementId,
                })
            );
            dispatch(
                fetchBonusSettlementsCustomerOverview({
                    id: +bonusSettlementId,
                    searchParams: searchParams,
                })
            );
            dispatch(fetchBonusSettlementsCustomerOverviewDetails(+bonusSettlementId));
        }
    };

    if (!details) {
        return null;
    }

    const handleExport = () => {
        if (bonusSettlementId && +bonusSettlementId) {
            window.open(urlJoin(API_BASE_URL, `/bonus-settlements/${bonusSettlementId}/customers-xlsx`), '_self');
        }
    };

    const isRunning = BonusSettlementStatus.Completed !== details.status;
    const canComplete = BonusSettlementStatus.ReadyToComplete === details.status;

    const menuActions: PaloOverflowAction[] = [
        ...(isRunning
            ? [
                  {
                      label: <FormattedMessage id="app.bonusSettlements.customerOverview.action.reloadAll" />,
                      onClick: () => setIsOpenReloadAll(true),
                  },
              ]
            : []),
        {
            label: <FormattedMessage id="app.bonusSettlements.customerOverview.action.exportAll" />,
            onClick: handleExport,
        },
    ];

    return (
        <>
            <PaloOverflowMenu actions={menuActions} />
            {isRunning && canComplete && (
                <Button
                    key="completeAll"
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => setIsOpenCompleteAll(true)}
                >
                    <FormattedMessage
                        id="app.bonusSettlements.customerOverview.action.completeAllWithNumber"
                        values={{ number: details.readyToCompleteCount }}
                    />
                </Button>
            )}
            <PaloNextThemeProvider>
                <PoolConfirmationModal
                    open={isOpenReloadAll}
                    isSuccessDialog={true}
                    title={<FormattedMessage id="app.bonusSettlements.customerOverview.action.reloadAll" />}
                    onCancel={() => setIsOpenReloadAll(false)}
                    onConfirm={onReloadAllCustomers}
                    action={<FormattedMessage id="app.bonusSettlements.customerOverview.action.reload" />}
                >
                    <PoolFormattedMessage
                        id="app.bonusSettlements.customerOverview.action.reloadAll.text"
                        values={{ number: details?.readyToCompleteCount }}
                    />
                </PoolConfirmationModal>
                <PoolConfirmationModal
                    open={isOpenCompleteAll}
                    isSuccessDialog={true}
                    title={<FormattedMessage id="app.bonusSettlementsSupplierOverview.action.completeAll" />}
                    onCancel={() => setIsOpenCompleteAll(false)}
                    onConfirm={onCompleteAllCustomers}
                    action={<FormattedMessage id="app.bonusSettlementsSupplierOverview.action.completeAll" />}
                >
                    <PoolFormattedMessage
                        id="app.bonusSettlements.customerOverview.action.completeAll.text"
                        values={{ number: details?.readyToCompleteCount }}
                    />
                </PoolConfirmationModal>

                <AddSupplierModal
                    open={isOpenAddSupplier}
                    bonusSettlementId={bonusSettlementId ? +bonusSettlementId : 0}
                    onClose={() => setIsOpenAddSupplier(false)}
                />
            </PaloNextThemeProvider>
        </>
    );
};
